import { ChangeDetectorRef, inject, Injectable, NgZone } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { UserService } from 'app/core/user/user.service';
import { MenuItem } from 'app/core/user/user.types';
import {
    compactNavigation,
    defaultNavigation,
    futuristicNavigation,
    horizontalNavigation,
} from 'app/mock-api/common/navigation/data';
import { cloneDeep } from 'lodash-es';
import { Subject, takeUntil } from 'rxjs';

import { environment } from 'environments/environment';

@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
    private readonly _futuristicNavigation: FuseNavigationItem[] =
    futuristicNavigation;
    private readonly _horizontalNavigation: FuseNavigationItem[] =
    horizontalNavigation;
    
    _compactNavigation: FuseNavigationItem[] = compactNavigation;
    _defaultNavigation: FuseNavigationItem[] = defaultNavigation;

    _menuByUserFromBE: FuseNavigationItem[] = null;
    _defaultMenuByUserFromBE: FuseNavigationItem[] = null;

    _menuByUser: FuseNavigationItem[] = null;
    _defaultMenuByUser: FuseNavigationItem[] = null;

    private _userService = inject(UserService);
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    /**
     * Constructor
     */
    constructor(
        private _fuseMockApiService: FuseMockApiService,
    ) {
        // Register Mock API handlers
        this.registerHandlers();
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ Navigation - GET
        // -----------------------------------------------------------------------------------------------------

        // Get the data from BE
        this._userService.menuByUser$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((menus: any) => {
                if (menus) {
                    const mapMenuIds = (items: any[]) => {
                        return items.map(item => {
                            const mapped = { ...item, id: item.menuId || item.id };
                            if (mapped.children && mapped.children.length > 0) {
                                mapped.children = mapMenuIds(mapped.children);
                            }
                            return mapped;
                        });
                    };
                    const mappedMenus = mapMenuIds(menus);
                    this._menuByUserFromBE = cloneDeep(mappedMenus) as FuseNavigationItem[];
                    this._defaultMenuByUserFromBE = cloneDeep(mappedMenus) as FuseNavigationItem[];
                }
            });


        this._fuseMockApiService.onGet('api/common/navigation').reply(() => {
            if (this._menuByUserFromBE && environment.production) {
                // Menu is retrieved from BE
                this._menuByUserFromBE.forEach((navItem) => {
                    this._defaultMenuByUserFromBE.forEach((defNavItem) => {
                        if(defNavItem.id === navItem.id) {
                            navItem.children = cloneDeep(defNavItem.children);
                        }
                    });
                });

                this._menuByUser = this._menuByUserFromBE;
                this._defaultMenuByUser = this._defaultMenuByUserFromBE;


                console.info("[Menu] User is currently using custom menu by role.");
            } else {
                // Menu failed to be retrieved or running locally therefore use meu FE

                this._compactNavigation.forEach((compactNavItem) => {
                    this._defaultNavigation.forEach((defNavItem) => {
                        if (defNavItem.id === compactNavItem.id) {
                            compactNavItem.children = cloneDeep(defNavItem.children);
                        }
                    });
                });

                this._menuByUser = this._compactNavigation;
                this._defaultMenuByUser = this._defaultNavigation;

                if (!environment.production) {
                    console.info("[Menu] Running locally. Using local mock menu.");
                } else {
                    console.error("[Menu] Custom menu retrival failed. Fallback to default menu.")
                }
            }

            // Return the response
            return [
                200,
                {
                    compact: cloneDeep(this._menuByUser),
                    default: cloneDeep(this._defaultMenuByUser),
                    futuristic: cloneDeep(this._futuristicNavigation),
                    horizontal: cloneDeep(this._horizontalNavigation),
                },
            ];
        });
    }
}
